import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { ReadPathCandidateDto, ReadPathLensDto } from '@common/synapse/introspection-types';
import ReadPathScoreBars from './ReadPathScoreBars';

export interface ReadPathLensPanelProps {
  open: boolean;
  loading: boolean;
  error: string | null;
  lens: ReadPathLensDto | null;
  turnIndex: number | null;
  onClose: () => void;
  onOpenClaim?: (claimId: string) => void;
}

type InjectedGroupId =
  | 'ucb_profile_only'
  | 'ucb_scored_not_selected'
  | 'ucb_and_retrieved'
  | 'retrieval_only';

const INJECTED_GROUP_ORDER: InjectedGroupId[] = [
  'ucb_profile_only',
  'ucb_scored_not_selected',
  'ucb_and_retrieved',
  'retrieval_only',
];

interface InjectionTag {
  label: string;
  color: 'default' | 'primary' | 'success' | 'info';
}

function hasRetrievalScores(candidate: ReadPathCandidateDto): boolean {
  return candidate.channels.length > 0 || candidate.totalScore > 0;
}

function classifyInjectedCandidate(candidate: ReadPathCandidateDto): InjectedGroupId {
  if (candidate.includedInUcb && candidate.includedInFinalPool) {
    return 'ucb_and_retrieved';
  }
  if (candidate.includedInUcb) {
    return hasRetrievalScores(candidate) ? 'ucb_scored_not_selected' : 'ucb_profile_only';
  }
  return 'retrieval_only';
}

function injectionTag(groupId: InjectedGroupId): InjectionTag {
  switch (groupId) {
    case 'ucb_profile_only':
      return { label: 'UCB only', color: 'default' };
    case 'ucb_scored_not_selected':
      return { label: 'UCB only · scored', color: 'primary' };
    case 'ucb_and_retrieved':
      return { label: 'UCB + retrieved', color: 'success' };
    case 'retrieval_only':
      return { label: 'Retrieved', color: 'info' };
  }
}

function compareCandidates(a: ReadPathCandidateDto, b: ReadPathCandidateDto): number {
  const scoreDelta = b.totalScore - a.totalScore;
  if (scoreDelta !== 0) {
    return scoreDelta;
  }
  return a.label.localeCompare(b.label);
}

function orderInjectedCandidates(candidates: ReadPathCandidateDto[]): Array<{
  candidate: ReadPathCandidateDto;
  groupId: InjectedGroupId;
}> {
  const buckets = new Map<InjectedGroupId, ReadPathCandidateDto[]>(
    INJECTED_GROUP_ORDER.map((id) => [id, []])
  );

  for (const candidate of candidates) {
    buckets.get(classifyInjectedCandidate(candidate))!.push(candidate);
  }

  const ordered: Array<{ candidate: ReadPathCandidateDto; groupId: InjectedGroupId }> = [];
  for (const groupId of INJECTED_GROUP_ORDER) {
    const items = buckets.get(groupId) ?? [];
    items.sort(compareCandidates);
    for (const candidate of items) {
      ordered.push({ candidate, groupId });
    }
  }
  return ordered;
}

function candidateCaption(candidate: ReadPathCandidateDto, groupId: InjectedGroupId): string {
  if (groupId === 'ucb_profile_only') {
    return `${candidate.objectType} · not scored this turn`;
  }
  if (hasRetrievalScores(candidate)) {
    return `${candidate.objectType} · score ${candidate.totalScore.toFixed(2)} · raw ${candidate.rawScore.toFixed(2)}`;
  }
  return candidate.objectType;
}

function CandidateCard({
  candidate,
  groupId,
  selected,
  onOpenClaim,
}: {
  candidate: ReadPathCandidateDto;
  groupId: InjectedGroupId | 'dropped';
  selected: boolean;
  onOpenClaim?: (claimId: string) => void;
}) {
  const clickable = candidate.objectType === 'claim' && onOpenClaim;
  const tag = groupId === 'dropped' ? null : injectionTag(groupId);
  const showRetrievalScores = groupId !== 'ucb_profile_only' && hasRetrievalScores(candidate);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: selected ? 'success.light' : 'divider',
        bgcolor: selected ? 'success.50' : 'background.paper',
        cursor: clickable ? 'pointer' : 'default',
      }}
      onClick={() => {
        if (clickable) {
          onOpenClaim(candidate.objectId);
        }
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between" useFlexGap>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {candidate.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {groupId === 'dropped'
              ? hasRetrievalScores(candidate)
                ? `${candidate.objectType} · score ${candidate.totalScore.toFixed(2)} · raw ${candidate.rawScore.toFixed(2)} · not injected`
                : `${candidate.objectType} · scored but not injected`
              : candidateCaption(candidate, groupId)}
          </Typography>
          {candidate.channels.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {candidate.channels.map((channel) => (
                <Chip key={channel} size="small" label={channel} variant="outlined" />
              ))}
            </Stack>
          )}
          {candidate.dropReason && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
              {candidate.dropReason}
            </Typography>
          )}
        </Box>
        {tag && (
          <Box sx={{ flexShrink: 0 }}>
            <Chip size="small" color={tag.color} label={tag.label} />
          </Box>
        )}
        {groupId === 'dropped' && (
          <Box sx={{ flexShrink: 0 }}>
            <Chip size="small" label="Dropped" />
          </Box>
        )}
      </Stack>
      {showRetrievalScores && (
        <Box sx={{ mt: 1.25 }}>
          <ReadPathScoreBars components={candidate.scoreComponents} compact />
        </Box>
      )}
    </Paper>
  );
}

function InjectedCandidatesList({
  candidates,
  onOpenClaim,
}: {
  candidates: ReadPathCandidateDto[];
  onOpenClaim?: (claimId: string) => void;
}) {
  const ordered = useMemo(() => orderInjectedCandidates(candidates), [candidates]);

  if (ordered.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Nothing was injected for this turn.
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {ordered.map(({ candidate, groupId }, index) => {
        const prevGroupId = index > 0 ? ordered[index - 1]!.groupId : null;
        const showGroupDivider = prevGroupId != null && prevGroupId !== groupId;

        return (
          <React.Fragment key={candidate.bundleItemId}>
            {showGroupDivider && <Divider sx={{ my: 0.5 }} />}
            <CandidateCard candidate={candidate} groupId={groupId} selected onOpenClaim={onOpenClaim} />
          </React.Fragment>
        );
      })}
    </Stack>
  );
}

const ReadPathLensPanel = ({
  open,
  loading,
  error,
  lens,
  turnIndex,
  onClose,
  onOpenClaim,
}: ReadPathLensPanelProps) => (
  <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">
          Read-path lens{turnIndex != null ? ` · Turn ${turnIndex}` : ''}
        </Typography>
        <IconButton onClick={onClose} aria-label="Close read-path lens">
          <CloseIcon />
        </IconButton>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        What the User Object contributed to this Synapse response.
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && lens && (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {!lens.snapshotAvailable && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Detailed read-path snapshot not logged for this turn. Only aggregate retrieval trace data is available.
            </Alert>
          )}

          {lens.retrievalMiss && lens.selectedCandidates.length === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Retrieval miss — no memory was injected for this turn.
            </Alert>
          )}
          {lens.retrievalMiss && lens.selectedCandidates.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Retrieval miss for this turn — injected context came from the profile User Context Block only.
            </Alert>
          )}

          {lens.ucbRenderedText && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                User Context Block (UCB)
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0, fontFamily: 'inherit' }}>
                  {lens.ucbRenderedText || '(empty)'}
                </Typography>
              </Paper>
            </>
          )}

          {lens.injectedContextText && lens.injectedContextText !== lens.ucbRenderedText && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                Full injected context
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                UCB plus any “Relevant this turn” lines compiled into the prompt.
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', m: 0, fontFamily: 'inherit' }}>
                  {lens.injectedContextText}
                </Typography>
              </Paper>
            </>
          )}

          <Typography variant="subtitle2" gutterBottom>
            Injected into prompt ({lens.selectedCandidates.length})
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Grouped by injection path; tags show how each item reached the prompt.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <InjectedCandidatesList candidates={lens.selectedCandidates} onOpenClaim={onOpenClaim} />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Dropped at retrieval ({lens.droppedCandidates.length})
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Scored for this turn but not injected. Profile-only items are not listed here.
          </Typography>
          <Stack spacing={1}>
            {lens.droppedCandidates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No dropped candidates recorded.
              </Typography>
            ) : (
              lens.droppedCandidates.map((candidate) => (
                <CandidateCard
                  key={candidate.bundleItemId}
                  candidate={candidate}
                  groupId="dropped"
                  selected={false}
                  onOpenClaim={onOpenClaim}
                />
              ))
            )}
          </Stack>
        </Box>
      )}
    </Box>
  </Drawer>
);

export default ReadPathLensPanel;
